import { NumberField } from "./ComposeLayers.js";
import { IN_EFFECT_OPTIONS, OUT_EFFECT_OPTIONS, EMPHASIS_EFFECT_OPTIONS } from "./ComposeSequence.js";
import "./ComposeAnimation.css";

/**
 * Applies a partial update to one existing segment (in/emphasis/out) of
 * a layer's animation, returning a new layer object. Shared by both the
 * form controls below and the timeline's drag interactions, so both
 * write through exactly the same logic - including dropping the whole
 * animation field once every part of it is empty, rather than leaving a
 * stale {} sitting on the layer.
 *
 * Only ever patches an existing segment, never creates one from scratch
 * - matches the original setInField/setEmphasisField/setOutField scope
 * exactly; creating a segment (setInEffect/setEmphasisEffect/toggleOut)
 * or removing one is a separate, deliberate action, untouched here.
 */
export function updateAnimationSegment(layer, segmentType, patch) {
  const anim = layer.animation;
  const existing = anim?.[segmentType];
  if (!existing) return layer;
  const next = { ...anim, [segmentType]: { ...existing, ...patch } };
  const clean = next.in || next.emphasis || next.out ? next : undefined;
  return { ...layer, animation: clean };
}

function SecondsField({ label, value, editable, onChange }) {
  return (
    <NumberField
      label={label}
      value={value}
      disabled={!editable}
      onChange={(v) => {
        if (!editable) return;
        onChange(Math.max(0, Math.min(30, Math.round(v * 10) / 10)));
      }}
    />
  );
}

/**
 * Entrance + optional emphasis + optional exit for a single, non-sequenced
 * layer - the direct sibling of SequenceEditor (that one replaces this
 * entirely once a layer switches to Sequence content).
 */
export default function LayerAnimationControls({ layer, editable, allowInfinite, onUpdateLayer }) {
  const anim = layer.animation;
  const inStep = anim?.in;
  const emphasis = anim?.emphasis;
  const out = anim?.out;
  const isCta = layer.role === "cta";

  // Drops the animation field entirely once every part of it is empty,
  // rather than leaving a stale {} sitting on the layer.
  const commit = (next) => {
    const clean = next.in || next.emphasis || next.out ? next : undefined;
    onUpdateLayer(layer.id, (l) => ({ ...l, animation: clean }));
  };

  const setInEffect = (val) => {
    if (val === "none") return commit({ ...anim, in: undefined });
    commit({ ...anim, in: { effect: val, delay: inStep?.delay ?? 0, duration: inStep?.duration ?? 0.6 } });
  };
  const setInField = (key, v) => {
    onUpdateLayer(layer.id, (l) => updateAnimationSegment(l, "in", { [key]: v }));
  };

  const setEmphasisEffect = (val) => {
    if (val === "none") return commit({ ...anim, emphasis: undefined });
    commit({
      ...anim,
      emphasis: { effect: val, delay: emphasis?.delay ?? 1, duration: emphasis?.duration ?? 0.8, times: emphasis?.times ?? 2 },
    });
  };
  const setEmphasisField = (patch) => {
    onUpdateLayer(layer.id, (l) => updateAnimationSegment(l, "emphasis", patch));
  };

  const toggleOut = () => commit({ ...anim, out: out ? undefined : { effect: "fade", start: 2, duration: 0.6 } });
  const setOutField = (patch) => {
    onUpdateLayer(layer.id, (l) => updateAnimationSegment(l, "out", patch));
  };

  return (
    <div className="layer-animation">
      <p className="layer-animation__title">Animation</p>

      <label className="layers-panel__field">
        <span>Entrance</span>
        <select
          value={inStep?.effect ?? "none"}
          disabled={!editable}
          onChange={(e) => setInEffect(e.target.value)}
          className="layers-panel__input"
        >
          <option value="none">None</option>
          {IN_EFFECT_OPTIONS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </label>
      {inStep ? (
        <div className="layer-animation__grid-2">
          <SecondsField label="Delay (s)" value={inStep.delay} editable={editable} onChange={(v) => setInField("delay", v)} />
          <SecondsField label="Duration (s)" value={inStep.duration} editable={editable} onChange={(v) => setInField("duration", v)} />
        </div>
      ) : null}

      <div className="layer-animation__section">
        <label className="layers-panel__field">
          <span>Emphasis</span>
          <select
            value={emphasis?.effect ?? "none"}
            disabled={!editable}
            onChange={(e) => setEmphasisEffect(e.target.value)}
            className="layers-panel__input"
          >
            <option value="none">None</option>
            {EMPHASIS_EFFECT_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        {emphasis ? (
          <div className="layer-animation__grid-3">
            <SecondsField label="Start (s)" value={emphasis.delay} editable={editable} onChange={(v) => setEmphasisField({ delay: v })} />
            <SecondsField label="Speed (s)" value={emphasis.duration} editable={editable} onChange={(v) => setEmphasisField({ duration: v })} />
            <label className="layers-panel__field">
              <span>Repeat</span>
              <select
                value={emphasis.times}
                disabled={!editable}
                onChange={(e) => setEmphasisField({ times: Number(e.target.value) })}
                className="layers-panel__input"
              >
                {[1, 2, 3].map((n) => (
                  <option key={n} value={n}>
                    {n}×
                  </option>
                ))}
                {allowInfinite ? <option value={0}>∞</option> : null}
              </select>
            </label>
          </div>
        ) : null}
      </div>

      {isCta ? (
        <p className="layer-animation__cta-note">
          The CTA stays visible in the held end frame — exit animations aren't allowed on it.
        </p>
      ) : (
        <div className="layer-animation__section">
          <label className="layer-animation__checkbox-field">
            <input type="checkbox" checked={!!out} disabled={!editable} onChange={toggleOut} />
            Exit animation
          </label>
          {out ? (
            <div className="layer-animation__out-body">
              <select
                value={out.effect}
                disabled={!editable}
                onChange={(e) => setOutField({ effect: e.target.value })}
                className="layers-panel__input"
              >
                {OUT_EFFECT_OPTIONS.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <div className="layer-animation__grid-2">
                <SecondsField label="Start at (s)" value={out.start} editable={editable} onChange={(v) => setOutField({ start: v })} />
                <SecondsField label="Duration (s)" value={out.duration} editable={editable} onChange={(v) => setOutField({ duration: v })} />
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}